/**
 * Activity Filters
 *
 * Filter controls for activity list.
 */

import { useMemo } from 'react';
import { Input } from 'reactstrap';
import { Button } from '@g17eco/ui-modern/components/ui/button';
import {
  EmissionScope,
  Scope1ActivityType,
  Scope2ActivityType,
  CalculationStatus,
} from '../types/emissions';
import { useGetAssetsQuery } from '../api/emissions';
import { FilterSelect, type FilterSelectOption } from './FilterSelect';

export interface ActivityFilterValues {
  search: string;
  scope: string;
  activityType: string;
  status: string;
  assetId: string;
  periodStart: string;
  periodEnd: string;
  /** Deep-link-only: when set, restricts the list to activities created by these BulkImport ids. Not surfaced in the filter UI. */
  bulkImportIds: string;
}

interface ActivityFiltersProps {
  initiativeId: string;
  filters: ActivityFilterValues;
  onChange: (filters: ActivityFilterValues) => void;
  onExport?: () => void;
}

const SCOPE_OPTIONS: FilterSelectOption[] = [
  { value: '', label: 'All Scopes' },
  { value: EmissionScope.Scope1, label: 'Scope 1' },
  { value: EmissionScope.Scope2, label: 'Scope 2' },
];

const ACTIVITY_TYPE_OPTIONS: FilterSelectOption[] = [
  { value: '', label: 'All Types' },
  // Scope 1
  { value: Scope1ActivityType.StationaryCombustion, label: 'Stationary Combustion' },
  { value: Scope1ActivityType.MobileCombustion, label: 'Mobile Combustion' },
  { value: Scope1ActivityType.FugitiveEmissions, label: 'Fugitive Emissions' },
  { value: Scope1ActivityType.ProcessEmissions, label: 'Process Emissions' },
  // Scope 2
  { value: Scope2ActivityType.PurchasedElectricity, label: 'Purchased Electricity' },
  { value: Scope2ActivityType.PurchasedHeating, label: 'Purchased Heating' },
  { value: Scope2ActivityType.PurchasedSteam, label: 'Purchased Steam' },
  { value: Scope2ActivityType.PurchasedCooling, label: 'Purchased Cooling' },
];

const STATUS_OPTIONS: FilterSelectOption[] = [
  { value: '', label: 'All Status' },
  { value: CalculationStatus.Calculated, label: 'Calculated' },
  { value: CalculationStatus.Pending, label: 'Pending' },
  { value: CalculationStatus.Failed, label: 'Failed' },
];

export const ActivityFilters = ({ initiativeId, filters, onChange, onExport }: ActivityFiltersProps) => {
  const handleChange = (field: keyof ActivityFilterValues, value: string) => {
    onChange({ ...filters, [field]: value });
  };

  const { data: assetsResponse } = useGetAssetsQuery({ initiativeId, limit: 100 });
  const assets = useMemo(() => assetsResponse?.data ?? [], [assetsResponse?.data]);

  const assetOptions = useMemo<FilterSelectOption[]>(
    () => [
      { value: '', label: 'All Assets' },
      { value: 'unassigned', label: 'Unassigned' },
      ...assets.map((asset) => ({ value: asset._id, label: asset.name })),
    ],
    [assets]
  );

  return (
    <div className='d-flex flex-wrap gap-2 mb-3 align-items-center'>
      {/* Search */}
      <div className='position-relative' style={{ minWidth: 250 }}>
        <i
          className='fa fa-search position-absolute text-ThemeTextLight'
          style={{ left: 12, top: '50%', transform: 'translateY(-50%)' }}
        />
        <Input
          type='text'
          placeholder='Search activities...'
          value={filters.search}
          onChange={(e) => handleChange('search', e.target.value)}
          className='ps-5 tw:text-xs'
        />
      </div>

      {/* Asset Filter */}
      <FilterSelect
        value={filters.assetId}
        options={assetOptions}
        onSelect={(next) => handleChange('assetId', next)}
        triggerClassName='tw:w-40'
      />

      {/* Scope Filter */}
      <FilterSelect
        value={filters.scope}
        options={SCOPE_OPTIONS}
        onSelect={(next) => handleChange('scope', next)}
        triggerClassName='tw:w-32'
      />

      {/* Activity Type Filter */}
      <FilterSelect
        value={filters.activityType}
        options={ACTIVITY_TYPE_OPTIONS}
        onSelect={(next) => handleChange('activityType', next)}
        triggerClassName='tw:w-[180px]'
      />

      {/* Status Filter */}
      <FilterSelect
        value={filters.status}
        options={STATUS_OPTIONS}
        onSelect={(next) => handleChange('status', next)}
        triggerClassName='tw:w-32'
      />

      {/* Spacer */}
      <div className='flex-grow-1' />

      {/* Export Button */}
      {onExport && (
        <Button variant='secondary' onClick={onExport}>
          <i className='fa fa-download me-1' />
          Export
        </Button>
      )}
    </div>
  );
};
